<template>
  <div class="tag-selector-multiple" :class="{ 'is-collapsible': collapsible, 'is-collapsed': collapsible && !expanded }">
    <!-- 可收合：標題列 -->
    <button
      v-if="collapsible"
      type="button"
      class="collapse-toggle"
      :disabled="disabled"
      @click="toggleExpanded"
    >
      <span class="collapse-toggle-left">
        <i class="ri-price-tag-3-line"></i>
        <span class="collapse-label">{{ label || '標籤' }}</span>
        <span class="collapse-count">({{ modelValue.length }})</span>
      </span>
      <i class="collapse-arrow" :class="expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
    </button>
    <label v-else-if="label" class="selector-label">{{ label }}</label>

    <!-- 收合時仍顯示已選標籤，方便確認 -->
    <div
      v-if="collapsible && !expanded && showSelected && modelValue.length > 0"
      class="selected-tags-section selected-tags-section--collapsed"
    >
      <div class="selected-tags">
        <span
          v-for="tagId in modelValue"
          :key="tagId"
          class="tag-badge"
        >
          {{ getTagName(tagId) }}
          <i class="ri-close-line" @click.stop="removeTag(tagId)"></i>
        </span>
      </div>
    </div>

    <div v-show="!collapsible || expanded" class="selector-body">
      <!-- 標籤搜尋框 -->
      <div v-if="!loading && availableTags.length > 0" class="tag-search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋標籤名稱..."
          @input="handleSearch"
          :disabled="disabled"
        />
        <i class="ri-search-line"></i>
      </div>

      <div v-if="loading" class="loading-state">
        <p>載入標籤中...</p>
      </div>

      <div v-else-if="availableTags.length === 0" class="empty-state">
        <p>目前沒有可用的標籤</p>
      </div>

      <div v-else-if="filteredTagCategories.length === 0" class="empty-state">
        <p>找不到符合的標籤</p>
      </div>

      <div v-else>
        <!-- 標籤分類頁籤 -->
        <div class="tag-tabs">
          <!-- 全部頁籤 -->
          <button
            type="button"
            class="tag-tab"
            :class="{ active: activeTab === 'all' }"
            @click.stop="handleTabClick('all')"
            :disabled="disabled"
          >
            <i class="ri-list-check"></i>
            <span>全部</span>
            <span class="tab-count">({{ allTagsCount }})</span>
          </button>
          <!-- 分類頁籤 -->
          <button
            type="button"
            v-for="category in filteredTagCategories"
            :key="category.key"
            class="tag-tab"
            :class="{ active: activeTab === category.key }"
            @click.stop="handleTabClick(category.key)"
            :disabled="disabled"
          >
            <i :class="category.icon"></i>
            <span>{{ category.label }}</span>
            <span class="tab-count">({{ category.tags.length }})</span>
          </button>
        </div>

        <!-- 當前頁籤的標籤列表 -->
        <div class="tag-tab-content">
          <!-- 全部標籤 -->
          <div
            v-show="activeTab === 'all'"
            class="tag-category-content"
          >
            <div class="tag-selector">
              <div
                v-for="tag in allFilteredTags"
                :key="tag.id"
                class="tag-item"
                :class="{ selected: modelValue.includes(tag.id) }"
                @click.stop="toggleTag(tag.id)"
              >
                <i :class="tag.tagIcon || 'ri-price-tag-3-line'" :style="{ color: tag.tagColor || '#666' }"></i>
                <span>{{ tag.tagName }}</span>
                <div v-if="modelValue.includes(tag.id)" class="check-icon">
                  <i class="ri-check-line"></i>
                </div>
              </div>
            </div>
          </div>
          <!-- 分類標籤 -->
          <div
            v-for="category in filteredTagCategories"
            :key="category.key"
            v-show="activeTab === category.key"
            class="tag-category-content"
          >
            <div class="tag-selector">
              <div
                v-for="tag in category.tags"
                :key="tag.id"
                class="tag-item"
                :class="{ selected: modelValue.includes(tag.id) }"
                @click.stop="toggleTag(tag.id)"
              >
                <i :class="tag.tagIcon || 'ri-price-tag-3-line'" :style="{ color: tag.tagColor || '#666' }"></i>
                <span>{{ tag.tagName }}</span>
                <div v-if="modelValue.includes(tag.id)" class="check-icon">
                  <i class="ri-check-line"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 已選擇的標籤區域 -->
      <div v-if="showSelected && modelValue.length > 0" class="selected-tags-section">
        <label class="selected-tags-label">已選擇的標籤 ({{ modelValue.length }})</label>
        <div class="selected-tags">
          <span
            v-for="tagId in modelValue"
            :key="tagId"
            class="tag-badge"
          >
            {{ getTagName(tagId) }}
            <i class="ri-close-line" @click.stop="removeTag(tagId)"></i>
          </span>
        </div>
      </div>
    </div>

    <p v-if="hint" class="form-hint">
      <i class="ri-information-line"></i>
      {{ hint }}
    </p>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { tagService } from '@/api/modules/tags.js';

export default {
  name: 'TagSelectorMultiple',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    showSelected: {
      type: Boolean,
      default: true
    },
    tagSource: {
      type: String,
      default: null // null 表示顯示互動、自動規則與專屬標籤；'manual'、'auto_rule'、'exclusive' 表示只顯示特定來源
    },
    collapsible: {
      type: Boolean,
      default: false
    },
    defaultExpanded: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const loading = ref(false);
    const searchQuery = ref('');
    const availableTags = ref([]);
    const activeTab = ref(null);
    const expanded = ref(props.collapsible ? props.defaultExpanded : true);

    // 載入標籤列表
    const loadTags = async () => {
      loading.value = true;
      try {
        const params = { isActive: true, pageSize: 1000 };
        if (props.tagSource) {
          params.tagSource = props.tagSource;
        }
        const response = await tagService.getTags(params);
        if (response && response.success && response.data) {
          const tags = Array.isArray(response.data) ? response.data : (response.data.items || []);
          // 選擇器不顯示系統標籤
          availableTags.value = tags.filter(tag => tag.tagSource !== 'system');
        }
      } catch (error) {
        console.error('載入標籤失敗:', error);
        availableTags.value = [];
      } finally {
        loading.value = false;
      }
    };

    // 標籤分類
    const tagCategories = computed(() => {
      const categories = {
        manual: {
          key: 'manual',
          label: '互動標籤',
          icon: 'ri-hand-heart-line',
          tags: []
        },
        auto_rule: {
          key: 'auto_rule',
          label: '自動規則標籤',
          icon: 'ri-robot-line',
          tags: []
        },
        exclusive: {
          key: 'exclusive',
          label: '專屬標籤',
          icon: 'ri-store-2-line',
          tags: []
        }
      };

      availableTags.value.forEach(tag => {
        const source = tag.tagSource || 'manual';
        if (categories[source]) {
          categories[source].tags.push(tag);
        } else {
          categories.manual.tags.push(tag);
        }
      });

      return Object.values(categories).filter(cat => cat.tags.length > 0);
    });

    // 過濾後的標籤分類（根據搜尋關鍵字）
    const filteredTagCategories = computed(() => {
      if (!searchQuery.value.trim()) {
        return tagCategories.value;
      }

      const query = searchQuery.value.toLowerCase().trim();
      return tagCategories.value.map(category => ({
        ...category,
        tags: category.tags.filter(tag =>
          tag.tagName.toLowerCase().includes(query) ||
          (tag.description && tag.description.toLowerCase().includes(query))
        )
      })).filter(category => category.tags.length > 0);
    });

    // 所有過濾後的標籤（用於「全部」頁籤）
    const allFilteredTags = computed(() => {
      if (!searchQuery.value.trim()) {
        return availableTags.value;
      }

      const query = searchQuery.value.toLowerCase().trim();
      return availableTags.value.filter(tag =>
        tag.tagName.toLowerCase().includes(query) ||
        (tag.description && tag.description.toLowerCase().includes(query))
      );
    });

    // 所有標籤的數量
    const allTagsCount = computed(() => {
      return allFilteredTags.value.length;
    });

    // 監聽分類變化，自動選擇「全部」作為預設頁籤
    watch(filteredTagCategories, (categories) => {
      if (categories.length > 0 && !activeTab.value) {
        activeTab.value = 'all';
      }
    }, { immediate: true });

    const toggleExpanded = () => {
      if (props.disabled) return;
      expanded.value = !expanded.value;
    };

    // 切換標籤選擇
    const toggleTag = (tagId) => {
      if (props.disabled) return;

      const currentValue = [...props.modelValue];
      const index = currentValue.indexOf(tagId);

      if (index > -1) {
        // 已選中，移除
        currentValue.splice(index, 1);
      } else {
        // 未選中，添加
        currentValue.push(tagId);
      }

      emit('update:modelValue', currentValue);
    };

    // 移除標籤
    const removeTag = (tagId) => {
      if (props.disabled) return;

      const currentValue = [...props.modelValue];
      const index = currentValue.indexOf(tagId);
      if (index > -1) {
        currentValue.splice(index, 1);
        emit('update:modelValue', currentValue);
      }
    };

    // 取得標籤名稱
    const getTagName = (tagId) => {
      const tag = availableTags.value.find(t => t.id === tagId);
      return tag ? (tag.tagName || tag.name) : tagId;
    };

    // 標籤搜尋
    const handleSearch = () => {
      // 即時過濾，不需要 API 呼叫
    };

    // 處理頁籤點擊
    const handleTabClick = (tabKey) => {
      if (props.disabled) return;
      activeTab.value = tabKey;
    };

    onMounted(() => {
      loadTags();
    });

    return {
      loading,
      searchQuery,
      availableTags,
      tagCategories,
      filteredTagCategories,
      allFilteredTags,
      allTagsCount,
      activeTab,
      expanded,
      toggleExpanded,
      toggleTag,
      removeTag,
      getTagName,
      handleSearch,
      handleTabClick
    };
  }
};
</script>

<style scoped>
.tag-selector-multiple {
  width: 100%;
}

.tag-selector-multiple.is-collapsible {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 0;
  overflow: hidden;
}

.collapse-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #f8fafc;
  border: none;
  border-bottom: 1px solid transparent;
  cursor: pointer;
  text-align: left;
  color: #333;
  transition: background 0.2s;
}

.tag-selector-multiple.is-collapsible:not(.is-collapsed) .collapse-toggle {
  border-bottom-color: #e5e7eb;
}

.collapse-toggle:hover:not(:disabled) {
  background: #f1f5f9;
}

.collapse-toggle:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.collapse-toggle-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.collapse-label {
  font-weight: 500;
  font-size: 14px;
}

.collapse-count {
  font-size: 13px;
  color: #64748b;
}

.collapse-arrow {
  font-size: 20px;
  color: #64748b;
  flex-shrink: 0;
}

.selector-body {
  padding: 0;
}

.tag-selector-multiple.is-collapsible .selector-body {
  padding: 14px;
}

.selector-label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

/* 標籤搜尋框 */
.tag-search-box {
  position: relative;
  margin-bottom: 16px;
}

.tag-search-box input {
  padding-right: 36px;
}

.tag-search-box i {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  pointer-events: none;
}

/* 載入和空狀態 */
.loading-state,
.empty-state {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* 標籤分類頁籤 */
.tag-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e9ecef;
  flex-wrap: wrap;
}

.tag-tab {
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: -2px;
}

.tag-tab:hover:not(:disabled) {
  color: #667eea;
}

.tag-tab.active {
  color: #667eea;
  border-bottom-color: #667eea;
  font-weight: 500;
}

.tag-tab:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tag-tab i {
  font-size: 16px;
}

.tab-count {
  font-size: 13px;
  color: #999;
  margin-left: 2px;
}

.tag-tab.active .tab-count {
  color: #667eea;
}

/* 標籤分類內容 */
.tag-tab-content {
  min-height: 120px;
}

.tag-category-content {
  animation: fadeIn 0.2s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* 標籤選擇器 */
.tag-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 2px solid #e9ecef;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  background: #fff;
}

.tag-item:hover:not(.disabled) {
  border-color: #667eea;
  background: #f8f9fa;
}

.tag-item.selected {
  border-color: #667eea;
  background: #e7f3ff;
}

.tag-item .check-icon {
  margin-left: 4px;
  color: #667eea;
}

.tag-item i[class^="ri-"] {
  font-size: 16px;
}

/* 已選擇的標籤區域 */
.selected-tags-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 2px solid #e9ecef;
}

.selected-tags-section--collapsed {
  margin-top: 0;
  padding: 12px 14px 14px;
  border-top: none;
}

.selected-tags-label {
  display: block;
  font-weight: 500;
  margin-bottom: 12px;
  color: #333;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #e6f7ff;
  color: #1890ff;
  border-radius: 20px;
  font-size: 13px;
  border: 1px solid #91d5ff;
}

.tag-badge i {
  cursor: pointer;
  font-size: 14px;
  transition: color 0.2s;
}

.tag-badge i:hover {
  color: #ff4d4f;
}

.form-hint {
  font-size: 13px;
  color: #666;
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.tag-selector-multiple.is-collapsible .form-hint {
  padding: 0 14px 14px;
  margin-top: 0;
}

.tag-selector-multiple.is-collapsible.is-collapsed .form-hint {
  padding-top: 0;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #1890ff;
}

.form-control:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}
</style>
